<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useSubscription, formatEur, type QuoteLine } from '@/composables/useSubscription'
import { useSubscriptionFlow } from '@/composables/useSubscriptionFlow'
import { formatStayDuration } from '@/subscriptionFlows/pmeBilling'

const { t } = useI18n()
const {
  locale,
  isCivil,
  isHealthExpenses,
  isAbroad,
  isArrivingFrance,
  isHealthTopUp,
  isRegionalAura,
  isSolidariteExam,
  duration,
  hasChosenProduct,
  lines: subLines,
  monthlyTotal: subMonthlyTotal,
  yearlyTotal: subYearlyTotal,
  stayDays,
  billedMonths,
} = useSubscription()

// For products wired to the API (health, abroad…) the whole quote (breakdown
// lines, totals and the subscription total) comes from the backend: prices from
// available_bundles and the total from compute_price. Other products keep the
// front-computed values.
const {
  hasFlow,
  lines: flowLines,
  monthlyTotal: flowMonthlyTotal,
  yearlyTotal: flowYearlyTotal,
  subscriptionTotal: flowTotal,
  subscriptionTotalLoading: flowTotalLoading,
  subscriptionTotalError: flowTotalError,
} = useSubscriptionFlow()

const lines = computed(() => (hasFlow.value ? flowLines.value : subLines.value))
const monthlyTotal = computed(() => (hasFlow.value ? flowMonthlyTotal.value : subMonthlyTotal.value))
const yearlyTotal = computed(() => (hasFlow.value ? flowYearlyTotal.value : subYearlyTotal.value))

/** Carte dépliée par défaut, repliable. */
const isOpen = ref(true)

const fmt = (v: number) => formatEur(v, locale.value)

/** Durée du séjour (étranger), formulée par le module PME — voir formatStayDuration. */
const stayDurationText = computed(() =>
  formatStayDuration(stayDays.value, billedMonths.value, t, locale.value),
)

function lineLabel(line: QuoteLine): string {
  if (line.kind === 'plan') {
    // Surcomplémentaire Tonic-Études (produit « renforcer-sante »).
    if (line.value === 'tonic') {
      return `${t('subscription.topUp.category')} ${t('subscription.topUp.name')}`
    }
    // Responsabilité civile : « Assurance Vie Étudiante Sécurité/Indispensable ».
    if (isCivil.value) {
      return `${t('subscription.civilResults.category')} ${t(`subscription.civilResults.products.${line.value}.name`)}`
    }
    // Étranger : « Assurance internationale Pack Europe/Monde ».
    if (isAbroad.value) {
      return `${t('subscription.abroadResults.category')} ${t(`subscription.abroadResults.products.${line.value}.name`)}`
    }
    // En France : « Assurance étudiant en France Pack ... ».
    if (isArrivingFrance.value) {
      return `${t('subscription.franceResults.category')} ${t(`subscription.franceResults.products.${line.value}.name`)}`
    }
    // Mutuelle AURA : « Complémentaire Santé Primordiale/Renforcée » d'après la clé
    // du bundle de base (back-end), repli sur le libellé de la formule.
    if (isRegionalAura.value) {
      const name = line.labelKey
        ? t(`bundles.${line.labelKey}`)
        : t(`subscription.regionalResults.products.${line.value}.name`)
      return `${t('subscription.regionalResults.category')} ${name}`
    }
    // Dépenses santé : « Health cover <formule> » d'après la clé du bundle de base.
    if (line.labelKey) {
      return `${t('subscription.results.category')} ${t(`bundles.${line.labelKey}`)}`
    }
    return `${t('subscription.results.category')} ${t(`subscription.results.tiers.${line.value}`)}`
  }
  if (line.kind === 'topUp') {
    // Health top-up (Tonic-Études) carries the backend bundle key, translated via
    // the `bundles` namespace; other journeys keep the hard-coded label.
    const name = line.labelKey ? t(`bundles.${line.labelKey}`) : t('subscription.topUp.name')
    return `${t('subscription.topUp.category')} ${name}`
  }
  if (line.kind === 'examInsurance') {
    return t('subscription.quote.examInsurance')
  }
  // Health add-ons carry the backend bundle key (translated via the `bundles`
  // namespace); other journeys keep the front's hard-coded add-on label.
  const addonName = line.labelKey
    ? t(`bundles.${line.labelKey}`)
    : t(`subscription.crossSell.addons.${line.value}.name`)
  return t('subscription.quote.addonLabel', { name: addonName })
}
</script>

<template>
  <section v-if="hasChosenProduct" class="quote" aria-labelledby="quote-title">
    <h2 id="quote-title" class="quote__title">
      <button
        type="button"
        class="quote__toggle"
        :aria-expanded="isOpen"
        aria-controls="quote-body"
        @click="isOpen = !isOpen"
      >
        <span>{{ t('subscription.quote.title') }}</span>
        <svg
          class="quote__chevron"
          :class="{ 'is-open': isOpen }"
          viewBox="0 0 20 20"
          width="16"
          height="16"
          aria-hidden="true"
        >
          <path d="M5 8l5 5 5-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    </h2>

    <div v-show="isOpen" id="quote-body" class="quote__body">
      <TransitionGroup tag="ul" name="quote-line" class="quote__lines">
        <li v-for="line in lines" :key="`${line.kind}:${line.value}`" class="quote__line">
          <span class="quote__line-label">{{ lineLabel(line) }}</span>
          <span v-if="!isAbroad && !isArrivingFrance" class="quote__line-price">
            <template v-if="!isCivil">
              <span class="quote__amount">{{ fmt(line.perMonth) }}</span>{{ t('subscription.results.perMonth') }}
              <span class="quote__sep">·</span>
            </template>
            <span class="quote__amount">{{ fmt(line.perYear) }}</span>{{ t('subscription.results.perYear') }}
          </span>
          <span v-else class="quote__line-price">
            <span class="quote__amount">{{ fmt(line.perMonth) }}</span>{{ t('subscription.results.perMonth') }}
          </span>
        </li>
      </TransitionGroup>

      <div v-if="!isCivil && !isAbroad && !isArrivingFrance" class="quote__total">
        <span class="quote__total-label">{{ t('subscription.quote.total') }}</span>
        <span class="quote__total-price">
          <span class="quote__amount">{{ fmt(monthlyTotal) }}</span>{{ t('subscription.results.perMonth') }}
          <span class="quote__sep">·</span>
          {{ fmt(yearlyTotal) }}{{ t('subscription.results.perYear') }}
        </span>
      </div>

      <!-- Étranger : total pour la durée du séjour calculé par le back-end
           (compute_price sur la plage de séjour) — ex. « 19 € pour 15 jours ». -->
      <Transition name="quote-pop">
        <div
          v-if="isAbroad && stayDurationText && (flowTotalLoading || flowTotalError || flowTotal !== null)"
          class="quote__subscription"
        >
          <span class="quote__subscription-label">{{ t('subscription.info.finalRecap.total') }}</span>
          <span v-if="flowTotalLoading" class="quote__subscription-price">
            {{ t('subscription.quote.computing') }}
          </span>
          <span v-else-if="flowTotalError" class="quote__subscription-error" role="alert">
            {{ t(flowTotalError) }}
          </span>
          <span v-else class="quote__subscription-price quote__subscription-price--stay">
            {{ t('subscription.info.finalRecap.priceForStay', { price: fmt(flowTotal!), duration: stayDurationText }) }}
          </span>
        </div>
      </Transition>

      <!-- En France : total pour la durée choisie, calculé par le back-end
           (compute_price sur début + durée) — ex. « 165 € pour 6 mois ». -->
      <Transition name="quote-pop">
        <div
          v-if="isArrivingFrance && duration && (flowTotalLoading || flowTotalError || flowTotal !== null)"
          class="quote__subscription"
        >
          <span class="quote__subscription-label">{{ t('subscription.info.finalRecap.total') }}</span>
          <span v-if="flowTotalLoading" class="quote__subscription-price">
            {{ t('subscription.quote.computing') }}
          </span>
          <span v-else-if="flowTotalError" class="quote__subscription-error" role="alert">
            {{ t(flowTotalError) }}
          </span>
          <span v-else class="quote__subscription-price">
            {{ t('subscription.info.finalRecap.priceForDuration', { price: fmt(flowTotal!), count: duration }) }}
          </span>
        </div>
      </Transition>

      <!-- Santé / surcomplémentaire / mutuelle AURA / responsabilité civile : total
           calculé par le back-end (compute_price) pour la formule choisie et la durée
           pré-sélectionnée (12 mois pour la civile, couverture annuelle). Tant qu'aucune
           date de début n'est posée, on l'estime sur une date par défaut (lendemain). -->
      <Transition name="quote-pop">
        <div
          v-if="(isCivil || isHealthExpenses || isHealthTopUp || isRegionalAura || isSolidariteExam) && (flowTotalLoading || flowTotalError || flowTotal !== null)"
          class="quote__subscription"
        >
          <span class="quote__subscription-label">
            {{ t('subscription.quote.subscriptionTotal', { count: duration }) }}
          </span>
          <span v-if="flowTotalLoading" class="quote__subscription-price">
            {{ t('subscription.quote.computing') }}
          </span>
          <span v-else-if="flowTotalError" class="quote__subscription-error" role="alert">
            {{ t(flowTotalError) }}
          </span>
          <span v-else class="quote__subscription-price">{{ fmt(flowTotal!) }}</span>
        </div>
      </Transition>
    </div>
  </section>
</template>

<style scoped>
.quote {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  background: color-mix(in oklab, var(--color-peach) 12%, white);
  border-radius: var(--radius-lg);
  padding: 1.25rem;
  border: 1px solid var(--primary);
  box-shadow: var(--shadow-sm);
}

.quote__title {
  margin: 0;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: var(--primary);
}
.quote__toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  width: 100%;
  margin: 0;
  padding: 0;
  background: none;
  border: 0;
  font: inherit;
  letter-spacing: inherit;
  text-transform: inherit;
  color: inherit;
  text-align: left;
  cursor: pointer;
}
.quote__toggle:focus-visible {
  outline: 2px solid var(--primary);
  outline-offset: 4px;
  border-radius: var(--radius-xs);
}
.quote__chevron {
  flex-shrink: 0;
  color: var(--primary);
  transition: transform var(--dur-fast) var(--ease-out);
}
.quote__chevron.is-open { transform: rotate(180deg); }

.quote__body {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.quote__lines {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  margin: 0;
  padding: 0;
  list-style: none;
}
.quote__line {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  padding-bottom: 0.6rem;
  border-bottom: 1px solid color-mix(in oklab, var(--border) 70%, transparent);
}
.quote__line-label {
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--color-chocolate);
  line-height: 1.3;
}
.quote__line-price {
  font-size: var(--text-xs);
  color: var(--text-muted);
}

.quote__amount {
  font-weight: 700;
  color: var(--color-chocolate);
}
.quote__sep {
  margin-inline: 0.15em;
  opacity: 0.6;
}

.quote__total {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}
.quote__total-label {
  font-size: var(--text-xs);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-muted);
}
.quote__total-price {
  font-size: var(--text-sm);
  color: var(--text-muted);
}

/* Encart mis en avant : total de la souscription pour la durée choisie. */
.quote__subscription {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  margin-top: 0.35rem;
  padding: 0.85rem 1rem;
  background: var(--primary);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-sm);
}
.quote__subscription-label {
  font-size: var(--text-xs);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-on-primary);
  opacity: 0.9;
}
.quote__subscription-price {
  font-size: var(--text-2xl);
  font-weight: 800;
  line-height: 1.1;
  color: var(--text-on-primary);
}
/* Le total du sejour est une phrase, pas un nombre : « 19 € pour 1 jour (0,5 mois
   facturé) » contre « 165 € pour 6 mois » ailleurs. A la taille commune il occupait
   quatre lignes dans la colonne, au point de ne plus se lire comme un total (SI-1473). */
.quote__subscription-price--stay {
  font-size: var(--text-lg);
  line-height: 1.25;
}
/* Message d'erreur compute_price (sur le fond accent de l'encart). */
.quote__subscription-error {
  font-size: var(--text-sm);
  font-weight: 600;
  line-height: 1.3;
  color: var(--text-on-primary);
}

/* Apparition de l'encart dès qu'une durée est choisie. */
.quote-pop-enter-active {
  transition: opacity var(--dur-base) var(--ease-out), transform var(--dur-base) var(--ease-spring);
}
.quote-pop-enter-from {
  opacity: 0;
  transform: translateY(-6px) scale(0.98);
}

/* Apparition « pas à pas » à chaque ajout de produit. */
.quote-line-enter-active {
  transition: opacity var(--dur-base) var(--ease-out), transform var(--dur-base) var(--ease-out);
}
.quote-line-enter-from {
  opacity: 0;
  transform: translateY(-6px);
}
</style>
